<template>
    <main class="dashboard fintoc-mismatches">
        <MainLayout
            :chartLoaded="true"
            @change-section="onSectionChange"
        />

        <div class="main-section underHeader">
            <div class="panel-shell">
                <!-- Encabezado: la lista de trabajo que deja la reconciliación
                     BD ↔ Fintoc (cron semanal / a demanda). -->
                <header class="panel-head">
                    <div class="panel-head__intro">
                        <p class="panel-eyebrow">Panel analista</p>
                        <h1 class="panel-title">Desfases Fintoc</h1>
                        <p class="panel-sub">
                            <template v-if="rows.length">
                                {{ rows.length }} conexiones desfasadas entre la app y Fintoc — revisalas caso por caso.
                            </template>
                            <template v-else-if="!tableData.loading">
                                Sin desfases pendientes.
                            </template>
                            <template v-if="lastReconciledAt">
                                Última reconciliación: {{ formatDateTime(lastReconciledAt) }}.
                            </template>
                        </p>
                    </div>
                    <div class="panel-actions">
                        <button
                            v-for="pill in verdictPills"
                            :key="pill.verdict"
                            type="button"
                            class="pill pill--filter"
                            :class="{ 'is-active': filterVerdict === pill.verdict }"
                            :title="pill.title"
                            @click="toggleVerdict(pill.verdict)"
                        >
                            {{ pill.filterLabel }}
                            <span class="pill__badge">{{ pill.count }}</span>
                        </button>
                        <button type="button" class="pill" @click="volverAlPanel">
                            Volver al panel
                        </button>
                        <button
                            type="button"
                            class="pill pill--accent"
                            :disabled="reconciling"
                            @click="reconciliarAhora"
                        >
                            {{ reconciling ? "Reconciliando…" : "Reconciliar ahora" }}
                        </button>
                    </div>
                </header>

                <!-- Desfases -->
                <div class="panel-card">
                    <div class="cartera-toolbar">
                        <div class="search-box">
                            <input
                                v-model="searchQuery"
                                type="search"
                                class="search-box__input"
                                placeholder="Buscar por empresa, RUT o link…"
                                aria-label="Buscar en los desfases"
                            />
                            <button
                                v-if="searchQuery"
                                type="button"
                                class="search-box__clear"
                                aria-label="Limpiar búsqueda"
                                @click="searchQuery = ''"
                            >×</button>
                        </div>
                        <span v-if="searchQuery || filterVerdict" class="cartera-toolbar__count">
                            {{ filteredData.length }} de {{ rows.length }} conexiones
                        </span>
                    </div>
                    <b-table
                        :data="filteredData"
                        :loading="tableData.loading"
                        ref="table"
                        :total="rows.length"
                        :show-detail-icon="false"
                        class="cartera-table"
                    >
                        <template #empty>
                            <div class="cartera-empty">
                                <template v-if="searchQuery || filterVerdict">
                                    Sin resultados para el filtro actual.
                                </template>
                                <template v-else-if="!tableData.loading">
                                    No hay desfases: la app y Fintoc están alineados. 🎉
                                </template>
                            </div>
                        </template>

                        <b-table-column field="company_name" cell-class="col col--company">
                            <template #header>
                                <span class="col-head col-head--static">Compañía</span>
                            </template>
                            <template #default="{ row }">
                                <button
                                    class="company-link"
                                    title="Abrir gestión de terceros"
                                    @click="openGestionTerceros(row)"
                                >
                                    {{ row.company_name }}
                                </button>
                                <div class="company-rut">{{ row.company_rut }}</div>
                            </template>
                        </b-table-column>

                        <b-table-column field="kind" cell-class="col col--product">
                            <template #header>
                                <span class="col-head col-head--static">Producto</span>
                            </template>
                            <template #default="{ row }">
                                <span class="type-tag">
                                    {{ row.kind === "SII" ? "🧾 SII" : "🏦 Banco" }}
                                </span>
                            </template>
                        </b-table-column>

                        <b-table-column field="verdict" cell-class="col col--verdict">
                            <template #header>
                                <span class="col-head col-head--static">Desfase</span>
                            </template>
                            <template #default="{ row }">
                                <span
                                    class="chip"
                                    :class="verdictBadge(row).cls"
                                    :title="verdictBadge(row).title"
                                >
                                    <span class="chip__dot"></span> {{ verdictBadge(row).label }}
                                </span>
                            </template>
                        </b-table-column>

                        <b-table-column field="link_id" cell-class="col col--link">
                            <template #header>
                                <span class="col-head col-head--static">Link</span>
                            </template>
                            <template #default="{ row }">
                                <span class="link-id" :title="row.link_id">{{ row.link_id }}</span>
                            </template>
                        </b-table-column>

                        <b-table-column field="used_at" cell-class="col col--dates">
                            <template #header>
                                <span class="col-head col-head--static">Última sync</span>
                            </template>
                            <template #default="{ row }">
                                <span class="date-cell">{{ formatDate(row.used_at) }}</span>
                            </template>
                        </b-table-column>

                        <b-table-column field="reconciled_at" cell-class="col col--dates">
                            <template #header>
                                <span class="col-head col-head--static">Reconciliado</span>
                            </template>
                            <template #default="{ row }">
                                <span class="date-cell">{{ formatDate(row.reconciled_at) }}</span>
                            </template>
                        </b-table-column>
                    </b-table>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import moment from "moment";
import { mapActions } from "vuex";

import MainLayout from "@/components/layout/MainLayout";
import GestionTerceros from "@/components/modals/GestionTerceros";

import { consoleHandlerError, notificate } from "@/utils/message";

// Chips por veredicto de desfase. `label` corto para la celda; `title` con la
// misma explicación larga que usa GestionTerceros; `filterLabel` para el pill.
const VERDICT_BADGES = {
    DB_OFF_FINTOC_ON: {
        cls: "is-bad",
        label: "Facturándose en Fintoc",
        filterLabel: "Facturándose",
        title: "Desconectado aquí pero sigue activo en Fintoc — desconéctalo para cortar el cobro",
    },
    DB_ON_FINTOC_OFF: {
        cls: "is-warn",
        label: "Datos congelados",
        filterLabel: "Datos congelados",
        title: "Conectado aquí pero inactivo en Fintoc — los datos están congelados",
    },
    MISSING_IN_FINTOC: {
        cls: "is-off",
        label: "Sin link en Fintoc",
        filterLabel: "Sin link",
        title: "El link ya no existe en Fintoc",
    },
};

export default {
    name: "FintocMismatches",
    components: {
        MainLayout,
    },
    data() {
        return {
            VERDICT_BADGES,
            rows: [],
            counts: {},
            lastReconciledAt: null,
            filterVerdict: null,
            searchQuery: "",
            reconciling: false,
            tableData: {
                loading: true,
            },
            modalDefaultUpdateSheet: {
                parent: this,
                hasModalCard: true,
                customClass: "modal-class",
                canCancel: ["escape", "outside"],
            },
        };
    },
    mounted() {
        this.loadData();
    },
    computed: {
        // Pills de filtro: solo los veredictos presentes en la lista.
        verdictPills() {
            return Object.entries(VERDICT_BADGES)
                .map(([verdict, badge]) => ({
                    verdict,
                    filterLabel: badge.filterLabel,
                    title: badge.title,
                    count: this.counts[verdict] || 0,
                }))
                .filter((pill) => pill.count > 0);
        },
        searchedData() {
            // Case- y tilde-insensitive, RUT sin puntos/guiones — mismo
            // criterio que la búsqueda del panel.
            const fold = (value) =>
                String(value || "")
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "");
            const query = fold(this.searchQuery.trim());
            if (!query) {
                return this.rows;
            }
            const rutQuery = query.replace(/[.\s-]/g, "");
            return this.rows.filter((row) => {
                if ([row.company_name, row.link_id].some((value) => fold(value).includes(query))) {
                    return true;
                }
                const rut = fold(row.company_rut).replace(/[.\s-]/g, "");
                return Boolean(rutQuery) && rut.includes(rutQuery);
            });
        },
        filteredData() {
            if (!this.filterVerdict) {
                return this.searchedData;
            }
            return this.searchedData.filter(
                (row) => row.verdict === this.filterVerdict
            );
        },
    },
    methods: {
        ...mapActions("analystCompany", [
            "fetchConnectionsMismatches",
            "reconcileAllConnections",
        ]),
        verdictBadge(row) {
            return VERDICT_BADGES[row.verdict] || {
                cls: "is-off",
                label: row.verdict,
                title: "",
            };
        },
        verdictCount(verdict) {
            return this.counts[verdict] || 0;
        },
        toggleVerdict(verdict) {
            this.filterVerdict = this.filterVerdict === verdict ? null : verdict;
        },
        formatDate(value) {
            return value ? moment(value).format("DD-MM-YYYY") : "—";
        },
        formatDateTime(value) {
            return value ? moment(value).format("DD-MM-YYYY HH:mm") : "—";
        },
        loadData() {
            this.tableData.loading = true;
            // Se retorna la chain (post-catch: nunca rechaza) para que los
            // tests puedan await-earla; los callers la ignoran.
            return this.fetchConnectionsMismatches().then((data) => {
                this.rows = [...data.rows];
                this.counts = data.counts || {};
                this.lastReconciledAt = data.last_reconciled_at;
            }).catch((error) => {
                consoleHandlerError({ error, prefix: "[fintocMismatches]", silent: true });
                notificate(
                    "No pudimos cargar los desfases. Revisa tu conexión e intenta de nuevo.",
                    "is-danger is-light"
                );
            }).finally(() => {
                this.tableData.loading = false;
            });
        },
        reconciliarAhora() {
            this.reconciling = true;
            return this.reconcileAllConnections().then(() => {
                notificate("Reconciliación completada.", "is-success is-light");
                return this.loadData();
            }).catch((error) => {
                consoleHandlerError({ error, prefix: "[fintocMismatches]", silent: true });
                notificate(
                    "Fintoc no respondió; reintenta en unos segundos.",
                    "is-danger is-light"
                );
            }).finally(() => {
                this.reconciling = false;
            });
        },
        openGestionTerceros(row) {
            this.$buefy.modal.open({
                ...this.modalDefaultUpdateSheet,
                component: GestionTerceros,
                canCancel: ["escape", "button", "outside"],
                props: { companyId: row.company_id },
                events: {
                    close: () => {
                        this.loadData();
                    },
                },
                // El analista pudo desconectar/reconciliar desde el modal:
                // recargar para que la fila resuelta salga de la lista.
                onCancel: () => {
                    this.loadData();
                },
                onClose: () => {
                    this.loadData();
                },
            });
        },
        volverAlPanel() {
            this.$router.push({ name: "panel-analista" });
        },
        onSectionChange(section, tab) {
            // Igual que PanelAnalista: no es una sección del sidebar.
            const query = { section };
            if (tab) query.tab = tab;
            this.$router.push({ path: "/dashboard", query }).catch((err) => {
                if (err.name !== "NavigationDuplicated") throw err;
            });
        },
    },
}
</script>

<style scoped lang="scss">
// ============================================================================
// Desfases Fintoc — mismo lenguaje visual que PanelAnalista (tema compartido
// var(--ap-*) de elements/_analyst_theme.scss); estilos acotados a esta vista.
// ============================================================================
.fintoc-mismatches {
    font-family: var(--ap-font);
    color: var(--ap-ink-2);
}

.panel-shell {
    max-width: 1240px;
    margin: 0 auto;
    padding: 32px 28px 56px;
}

.panel-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
    margin-bottom: 24px;
}

.panel-eyebrow {
    margin: 0 0 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--ap-teal);
}

.panel-title {
    margin: 0;
    font-family: var(--ap-font);
    font-weight: 700;
    font-size: 30px;
    line-height: 1.05;
    letter-spacing: -0.02em;
    color: var(--ap-ink);
}

.panel-sub {
    margin: 8px 0 0;
    font-size: 14px;
    color: var(--ap-slate);
}

.panel-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 18px;
    border: 1px solid var(--ap-line-strong);
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-paper);
    color: var(--ap-ink-2);
    font-family: var(--ap-font);
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.16s var(--ap-ease), border-color 0.16s var(--ap-ease),
        background-color 0.16s var(--ap-ease), box-shadow 0.16s var(--ap-ease);

    &:hover {
        transform: translateY(-1px);
        border-color: var(--ap-teal);
        color: var(--ap-teal-deep);
        box-shadow: 0 6px 18px -10px rgba(14, 47, 52, 0.5);
    }

    &:active {
        transform: translateY(0);
    }

    &:focus-visible {
        outline: 2px solid var(--ap-teal);
        outline-offset: 2px;
    }

    &:disabled {
        opacity: 0.6;
        cursor: wait;
        transform: none;
    }
}

.pill--filter {
    &.is-active {
        border-color: var(--ap-alert);
        background-color: var(--ap-alert-wash);
        color: var(--ap-alert);
    }
}

.pill--accent {
    border-color: transparent;
    background-color: var(--ap-ink);
    color: var(--ap-paper);

    &:hover {
        color: var(--ap-paper);
        background-color: var(--ap-teal-deep);
        border-color: transparent;
    }
}

.pill__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-alert);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.panel-card {
    background-color: var(--ap-paper);
    border: 1px solid var(--ap-line);
    border-radius: var(--ap-radius);
    box-shadow: var(--ap-shadow-card);
    overflow: hidden;
}

.cartera-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--ap-line);
}

.search-box {
    position: relative;
    flex: 0 1 340px;
}

.search-box__input {
    width: 100%;
    padding: 8px 32px 8px 12px;
    font-family: var(--ap-font);
    font-size: 14px;
    color: var(--ap-ink-2);
    background-color: var(--ap-paper);
    border: 1px solid var(--ap-line);
    border-radius: var(--ap-radius-pill);
    transition: border-color 0.16s var(--ap-ease), box-shadow 0.16s var(--ap-ease);

    &::placeholder {
        color: var(--ap-ink-3, #98a2b3);
    }

    &:focus {
        outline: none;
        border-color: var(--ap-teal);
        box-shadow: 0 0 0 3px rgba(14, 116, 144, 0.12);
    }
}

.search-box__clear {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--ap-ink-2);
    font-size: 16px;
    line-height: 1;
    cursor: pointer;

    &:hover {
        background-color: var(--ap-line);
    }
}

.cartera-toolbar__count {
    font-size: 13px;
    color: var(--ap-ink-2);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

:deep(.cartera-table){
    font-family: var(--ap-font);

    .table {
        background-color: transparent;
        color: var(--ap-ink-2);
        width: 100%;
    }

    thead th {
        border: none;
        border-bottom: 1px solid var(--ap-line);
        background-color: var(--ap-mist);
        padding: 0;
        vertical-align: middle;
    }

    tbody td {
        border: none;
        border-bottom: 1px solid var(--ap-line);
        padding: 16px 18px;
        vertical-align: middle;
        font-size: 13.5px;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }

    tbody tr {
        transition: background-color 0.14s var(--ap-ease);
    }

    tbody tr:hover {
        background-color: var(--ap-teal-wash);
    }
}

:deep(.col-head){
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 13px 18px;
    border: none;
    background: transparent;
    font-family: var(--ap-font);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ap-muted);
}

:deep(.col--company){
    width: 26%;
    min-width: 200px;
}

:deep(.col--product){
    width: 10%;
    white-space: nowrap;
}

:deep(.col--verdict){
    width: 26%;
    min-width: 220px;
}

:deep(.col--link){
    width: 18%;
}

:deep(.col--dates){
    width: 10%;
    white-space: nowrap;
}

:deep(.company-link){
    border: none;
    background: transparent;
    padding: 0;
    text-align: left;
    font-family: var(--ap-font);
    font-size: 14px;
    font-weight: 600;
    color: var(--ap-ink);
    cursor: pointer;
    border-bottom: 1px solid transparent;
    transition: color 0.14s var(--ap-ease), border-color 0.14s var(--ap-ease);

    &:hover {
        color: var(--ap-teal-deep);
        border-bottom-color: rgba(13, 125, 128, 0.4);
    }
}

:deep(.company-rut){
    margin-top: 2px;
    font-size: 12px;
    color: var(--ap-muted);
    font-variant-numeric: tabular-nums;
}

:deep(.type-tag){
    display: inline-block;
    padding: 3px 10px;
    border: 1px solid var(--ap-line-strong);
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-mist);
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--ap-slate);
    white-space: nowrap;
}

:deep(.link-id){
    font-size: 12px;
    color: var(--ap-slate);
    font-variant-numeric: tabular-nums;
    word-break: break-all;
}

:deep(.date-cell){
    font-size: 12.5px;
    color: var(--ap-slate);
    font-variant-numeric: tabular-nums;
}

:deep(.chip){
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: var(--ap-radius-pill);
    font-size: 11.5px;
    font-weight: 500;
    white-space: nowrap;
    border: 1px solid transparent;

    .chip__dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        flex-shrink: 0;
    }

    &.is-warn {
        background-color: var(--ap-warn-wash);
        color: var(--ap-warn-ink);
        border-color: rgba(200, 138, 0, 0.25);

        .chip__dot {
            background-color: var(--ap-warn);
        }
    }

    &.is-bad {
        background-color: var(--ap-alert-wash);
        color: var(--ap-alert);
        border-color: rgba(192, 69, 59, 0.25);

        .chip__dot {
            background-color: var(--ap-alert);
        }
    }

    &.is-off {
        background-color: var(--ap-mist);
        color: var(--ap-muted);
        border-color: var(--ap-line);

        .chip__dot {
            background-color: #c4d0d1;
        }
    }
}

.cartera-empty {
    padding: 48px 0;
    text-align: center;
    color: var(--ap-muted);
    font-size: 14px;
}

@media (prefers-reduced-motion: reduce) {
    .pill,
    :deep(.cartera-table tbody tr),
    :deep(.company-link){
        transition: none;
    }
}
</style>
